import { getQuickFillDataGrpc } from '../../api/grpc/quickFillClient';
import type { RequestedParameterIntentInput } from '../../api/grpc/requestedParameterIntent';
import { normalizeRevitDataType } from '../../api/revit/revitDataType';
import type { RevitElementCategory, RevitFamily } from '../../api/revit/types';
import type { RevitOperationProgressSnapshot } from '../../api/grpc/operationProgressClient';

export interface DefinitionOnlyFamilySnapshotResult {
  families: RevitFamily[];
  availableParameters: unknown[];
  available: boolean;
  error?: string;
}

function mapFamily(input: any): RevitFamily {
  return {
    id: String(input?.id ?? ''),
    name: String(input?.name ?? ''),
    category: String(input?.category ?? 'Unknown') as RevitElementCategory,
    isEditable: typeof input?.isEditable === 'boolean' ? input.isEditable : undefined,
    isSystemFamily: typeof input?.isSystemFamily === 'boolean' ? input.isSystemFamily : undefined,
    parameters: [],
    types: Array.isArray(input?.types)
      ? input.types.map((type: any) => ({
          id: String(type?.id ?? ''),
          name: String(type?.name ?? ''),
          parameterValues: Array.isArray(type?.parameterValues)
            ? type.parameterValues.map((parameter: any) => ({
                parameterId: String(parameter?.parameterId ?? ''),
                parameterName: String(parameter?.parameterName ?? ''),
                value: parameter?.value ?? '',
                isReadOnly: Boolean(parameter?.isReadOnly),
                isInstance: Boolean(parameter?.isInstance),
                dataType: normalizeRevitDataType(parameter?.dataType),
              }))
            : [],
        }))
      : [],
  };
}

/**
 * Authoritative family-definition evidence for parameter compliance verdicts
 * and family-destination write planning. The broad family snapshot's instance
 * sweep samples placed elements, so a project-bound parameter looks "already
 * present" on families that never received it. The definition-only readiness
 * shape replaces swept rows with family-document definitions, which is exactly
 * the evidence an every-family verdict or family-destination write must use.
 */
export async function fetchDefinitionOnlyFamilySnapshot(input: {
  parameterNames: string[];
  requestedParameters: RequestedParameterIntentInput[];
  categoryNames?: string[];
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}): Promise<DefinitionOnlyFamilySnapshotResult> {
  if (input.parameterNames.length === 0) {
    return { families: [], availableParameters: [], available: true };
  }
  const result = await getQuickFillDataGrpc(input.parameterNames, {
    snapshotShape: 'familyType',
    ...(input.categoryNames && input.categoryNames.length > 0
      ? { dataScope: { categoryNames: input.categoryNames } }
      : {}),
    deferUnplacedFamilyCatalog: true,
    requestedParameters: input.requestedParameters,
    mappingReadinessOnly: true,
    ...(input.onProgress ? { onProgress: input.onProgress } : {}),
  });
  if (!result.success) {
    return {
      families: [],
      availableParameters: [],
      available: false,
      error: result.error || 'Family definition inspection failed',
    };
  }
  if (result.mappingReadinessScoped !== true) {
    return {
      families: [],
      availableParameters: [],
      available: false,
      error: 'Reload the updated Revit connector DLL before retrying parameter setup.',
    };
  }
  return {
    families: (Array.isArray(result.families) ? result.families : []).map(mapFamily),
    availableParameters: Array.isArray(result.availableParameters)
      ? result.availableParameters
      : [],
    available: true,
  };
}
